/* eslint-disable complexity */
import { useLocation } from '@reach/router';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import isEqual from 'lodash/isEqual';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchAnyPattern } from 'src/components/blocks/utils';
import { usePageStore } from 'src/stores/page';

let brazeMockData: any;

// TODO: Refactor env checks to use a more consistent approach
const isDevBuild = process.env.NODE_ENV === 'development';
if (isDevBuild) {
  import('./brazeMockData').then(mod => {
    brazeMockData = mod.brazeMockData;
  });
}

export type BrazeCardPayload = {
  id: string;
  title: string;
  description: string;
  url: string;
  linkText: string;
  created?: string | Date;
  isControl: boolean;
  imageUrl: string;
  extras: {
    location: string;
    format: string;
    brand?: string;
    species?: string;
    path?: string;
    priority: string;
    campaign: string;
    add_desc?: string;
    flags?: 'popular' | 'redeem_again' | 'for_you';
    reward_id?: string;
  };
};

type useBrazeProps = {
  location?: string;
};

export const FORMAT_BUCKETS = {
  banner: ['web_banner', 'web_captioned_banner'],
  card: ['web_classic_card', 'web_captioned_card'],
};

export function testRegexMatch(pattern: string | undefined, key: string): boolean {
  if (!pattern) return false;
  try {
    const regex = new RegExp(pattern);
    return regex.test(key);
  } catch (e) {
    return pattern === key;
  }
}

export const useBrazeResults = ({
  search_results,
  brazeCardSlots,
  limitSlots = 2,
}: {
  search_results: DrupalSearch['search_results'];
  brazeCardSlots: IStorageBrazeCard[] | undefined;
  limitSlots?: number;
}) => {
  const routerLocation = useLocation().pathname;
  const resultsWithBrazeCards = useMemo(() => {
    const newResults: any[] = search_results?.slice() || [];
    // Limit to 2 cards.
    const cards = brazeCardSlots
      ?.filter(card => !card.field_formats?.includes('banner'))
      ?.filter(card => {
        if (!card.field_url_visibility || card.field_url_visibility === '') return true;
        return matchAnyPattern(routerLocation, card.field_url_visibility);
      })
      .slice(0, limitSlots);
    if (newResults?.length && cards?.length) {
      const [first, second] = cards;
      // Insert first card as 5th result.
      if (newResults?.length >= 4 && first) {
        newResults.splice(4, 0, first);
      }
      // Insert second card as 12th result.
      if (newResults?.length >= 11 && second) {
        newResults.push(second);
      }
    }
    return newResults;
  }, [search_results, brazeCardSlots, limitSlots, routerLocation]);

  return resultsWithBrazeCards;
};

export const useBraze = ({ location }: useBrazeProps) => {
  const { pathname } = useLocation();
  const [brazeData, setBrazeData] = useState<BrazeCardPayload[]>([]);
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [isBrazeDataLoaded, setIsBrazeDataLoaded] = useState(false);
  const [contentCards, setContentCards] = useState<any[]>([]);
  const [brazeSDK, setBrazeSDK] = useState<any | null>(null);
  const brazeContext = usePageStore(state => state.page);
  const { brand, species } = brazeContext;

  const sortContentCards = useCallback(() => {
    return contentCards
      .filter((card: BrazeCardPayload) => {
        // Defaults to hiding the card if locations is not present
        if (!card.extras.location) return false;
        if (card.extras.location === 'all_com') return true;

        const locations = card.extras.location?.split(',');

        if (location && locations.includes(location)) {
          return true;
        }

        return false;
      })
      .filter((card: BrazeCardPayload) => {
        if (!card.extras.path) return true;
        return testRegexMatch(card.extras.path, pathname);
      })
      .filter((card: BrazeCardPayload) => {
        if (!card.extras.brand) return true;
        return testRegexMatch(card.extras.brand, brand ?? 'brand_agnostic');
      })
      .filter((card: BrazeCardPayload) => {
        // If species is not present in the card, add it to the list
        if (!card.extras.species) return true;
        // If we cant derive species from the argument, add the card
        if (species && species.toLowerCase() === card.extras.species.toLowerCase()) return true;

        return false;
      })
      .sort((a: BrazeCardPayload, b: BrazeCardPayload) => {
        const aBrandMatch = testRegexMatch(a.extras.brand, brand || '');
        const bBrandMatch = testRegexMatch(b.extras.brand, brand || '');

        const aSpeciesMatch = species?.toLowerCase() === a.extras.species?.toLowerCase();
        const bSpeciesMatch = species?.toLowerCase() === b.extras.species?.toLowerCase();

        // Prioritize match on both brand and species
        if (aBrandMatch && aSpeciesMatch && !(bBrandMatch && bSpeciesMatch)) return -1;
        if (bBrandMatch && bSpeciesMatch && !(aBrandMatch && aSpeciesMatch)) return 1;

        // Prioritize match on brand.
        if (aBrandMatch && !bBrandMatch) return -1;
        if (bBrandMatch && !aBrandMatch) return 1;

        // Prioritize match on species.
        if (aSpeciesMatch && !bSpeciesMatch) return -1;
        if (bSpeciesMatch && !aSpeciesMatch) return 1;

        // Sort by priority and then by created date
        if (a.extras.priority === b.extras.priority && a.created && b.created) {
          const aDate = new Date(a.created);
          const bDate = new Date(b.created);

          return bDate.getTime() - aDate.getTime();
        }

        if (!a.extras.priority) return 1;
        if (!b.extras.priority) return -1;

        return Number(a.extras.priority) - Number(b.extras.priority);
      });
  }, [contentCards, brand, species, location, pathname]);

  if (process.env.GATSBY_GA_LOGGING === 'true' && typeof window !== 'undefined') {
    // eslint-disable-next-line
    console.groupCollapsed(`%cBraze Data`, 'color: #5353fa');
    // eslint-disable-next-line
    console.log({ brazeData });
    // eslint-disable-next-line
    console.groupEnd();
  }

  useEffect(() => {
    // Poll for Braze SDK and give up after 5 minutes
    let counter = 0;
    const brazePoll = setInterval(() => {
      counter++;
      const sdkLoaded = window?.braze;
      if (sdkLoaded || counter > 300) {
        clearInterval(brazePoll);
      }
      if (sdkLoaded) setIsSdkLoaded(true);
    }, 1000);
  }, []);

  useEffect(() => {
    if (isSdkLoaded) {
      const {
        getCachedContentCards,
        openSession,
        removeSubscription,
        requestContentCardsRefresh,
        subscribeToContentCardsUpdates,
      } = window.braze;

      const contentCardSubscription = subscribeToContentCardsUpdates(
        ({ cards }: { cards: BrazeCardPayload[] }) => {
          setIsBrazeDataLoaded(true);

          if (!cards.length) return;
          setContentCards([...cards]);
        },
      );

      openSession();
      const cachedCards = getCachedContentCards()?.cards ? getCachedContentCards().cards : [];
      const initialCards = isDevBuild && brazeMockData ? brazeMockData.cards : cachedCards;

      requestContentCardsRefresh();
      setContentCards(initialCards);
      setBrazeSDK(window.braze);

      return () => {
        // Cleanup: unsubscribe from content card updates
        removeSubscription(contentCardSubscription);
      };
    }
    return undefined;
  }, [isSdkLoaded, setContentCards]);

  useEffect(() => {
    if (contentCards.length) {
      setIsBrazeDataLoaded(true);
      setBrazeData(sortContentCards());
    }
  }, [contentCards, sortContentCards]);

  return { brazeData, isBrazeDataLoaded, isSdkLoaded, brazeSDK };
};

export const BrazeContext = createContext<{
  brazeCardPayloads: BrazeCardPayload[];
  brazeCardSlots: IStorageBrazeCard[];
  loaded: boolean;
  location: string;
}>({
  brazeCardPayloads: [],
  brazeCardSlots: [],
  loaded: false,
  location: '',
});

export const BrazeProvider: React.FC<{
  children: React.ReactNode;
  location: string;
  allStorageBrazeCard: AllStorageBrazeCards;
}> = ({ children, location, allStorageBrazeCard }) => {
  const routerLocation = useLocation().pathname;

  const brazeCardSlots = useMemo(() => {
    const cards = allStorageBrazeCard.edges.map(edge => edge.node);
    const filtered = cards
      ?.filter(card => !card.field_formats?.includes('banner'))
      ?.filter(card => {
        if (!card.field_url_visibility || card.field_url_visibility === '') return true;
        return matchAnyPattern(routerLocation, card.field_url_visibility);
      });

    return filtered;
  }, [allStorageBrazeCard, routerLocation]);

  const { brazeData: brazeCardPayloads, isBrazeDataLoaded } = useBraze({
    location,
  });

  const value = useMemo(() => {
    return { brazeCardPayloads, brazeCardSlots, loaded: isBrazeDataLoaded, location };
  }, [brazeCardPayloads, brazeCardSlots, location, isBrazeDataLoaded]);

  return <BrazeContext.Provider value={value}>{children}</BrazeContext.Provider>;
};

export const useBrazeContext = () => React.useContext(BrazeContext);

/**
 * A custom hook that observes when a Braze content card element becomes visible in the viewport
 * and triggers impression tracking.
 *
 * @param elementRef - React ref object pointing to the HTML element to observe
 * @param brazeData - The Braze card payload data containing tracking information
 */
export const useBrazeObserver = (
  elementRef: React.RefObject<HTMLElement>,
  brazeData: BrazeCardPayload | undefined,
) => {
  const { loaded } = useBrazeContext();
  const prevBrazeDataRef = useRef<BrazeCardPayload>();

  // Prevent re-running effect every time the braze cards sync with the same data
  const memoizedBrazeData = useMemo(() => {
    if (isEqual(prevBrazeDataRef.current, brazeData)) {
      return prevBrazeDataRef.current;
    }

    prevBrazeDataRef.current = brazeData;
    return brazeData;
  }, [brazeData]);

  useEffect(() => {
    if (!loaded || !elementRef.current || !memoizedBrazeData) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.7) {
            const { logContentCardImpressions } = window.braze;
            logContentCardImpressions(memoizedBrazeData);

            sendIt({
              event: 'content_card_impression',
              eventLabel: 'content_card_impression',
              eventCategory: 'braze',
              eventAction: 'impression',
              eventParams: {
                braze_format: memoizedBrazeData.extras.format,
                braze_location: memoizedBrazeData.extras.location,
                braze_campaign: memoizedBrazeData.extras.campaign,
                braze_priority: memoizedBrazeData.extras.priority,
              },
            });

            observer.disconnect();
          }
        });
      },
      { threshold: 0.7, rootMargin: '0px' },
    );

    observer.observe(elementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, loaded, memoizedBrazeData]);
};

/** Used to handle braze "card" clicks
 *
 * @returns A callback function for handling Braze content card clicks
 * */
export const useBrazeClick = () => {
  const { loaded } = useBrazeContext();

  return (brazeData: BrazeCardPayload | undefined) => {
    if (!brazeData || !loaded || !window.braze) return;

    const { logContentCardClick } = window.braze;
    logContentCardClick(brazeData);

    sendIt({
      event: 'content_card_click',
      eventLabel: 'content_card_click',
      eventCategory: 'braze',
      eventAction: 'click',
      eventParams: {
        braze_format: brazeData.extras?.format,
        braze_location: brazeData.extras?.location,
        braze_campaign: brazeData.extras?.campaign,
        braze_priority: brazeData.extras?.priority,
      },
    });
  };
};

/**
 * Helper functions
 **/
export const getBrazeBanners = (brazeCards?: IStorageBrazeCard[]) =>
  brazeCards?.filter(card => card.field_formats?.includes('banner'));

export const getBrazeListingCards = (allBrazeCards: BrazeCardPayload[]) =>
  allBrazeCards.filter(card => FORMAT_BUCKETS.card.includes(card.extras.format));
